/**
 * Client-side оптимизация картинок ПЕРЕД upload'ом.
 *
 * Зачем: iPhone-фото 4-12 MB не имеет смысла слать целиком, если бэк всё равно
 * ужимает до 2400px / JPEG q85. Делаем resize+recompress прямо в браузере →
 * экономия 70-90% трафика, особенно на мобильном интернете.
 *
 * Также:
 * - HEIC/HEIF (iPhone "High Efficiency") конвертим в JPEG, иначе на Windows/
 *   Android картинку не покажет (Safari умеет, остальные — нет)
 * - Сохраняем EXIF orientation (browser-image-compression делает auto-rotate)
 * - Видео НЕ трогаем (transcode на десктопе через ffmpeg.wasm требует ~15MB
 *   wasm-бандла — не оправдано). Бэкенд сам faststart'ит и при нужде
 *   re-encode'ит в h264.
 */
type CompressOptions = {
  maxSizeMB?: number;
  maxWidthOrHeight?: number;
  useWebWorker?: boolean;
  initialQuality?: number;
  preserveExif?: boolean;
};

const MAX_WIDTH = 2400;        // совпадает с backend resize (нет смысла слать больше)
const MAX_SIZE_MB = 1.5;       // целевой размер после компрессии
const QUALITY = 0.85;


/** True если файл — HEIC/HEIF (iPhone "High Efficiency"). */
function isHeic(file: File): boolean {
  const m = (file.type || "").toLowerCase();
  if (m === "image/heic" || m === "image/heif") return true;
  const f = file.name.toLowerCase();
  return f.endsWith(".heic") || f.endsWith(".heif");
}


/** True если файл — обычная картинка (PNG/JPEG/WebP/GIF), которую можно
 *  ужимать через canvas. Не включает HEIC (для него отдельная конвертация). */
function isCompressibleImage(file: File): boolean {
  const m = (file.type || "").toLowerCase();
  if (m.startsWith("image/")) {
    return m !== "image/gif" && m !== "image/svg+xml" && !isHeic(file);
  }
  return /\.(png|jpe?g|webp|bmp)$/.test(file.name.toLowerCase());
}


/** Конвертит HEIC/HEIF → JPEG через heic2any (динамический import).
 *  Если конверсия не удалась — возвращает оригинал (бэк попробует). */
async function convertHeicToJpeg(file: File): Promise<File> {
  try {
    // dynamic import — heic2any весит ~150KB, грузим только если HEIC попался
    const mod = await import("heic2any");
    const heic2any = mod.default;
    const blob = await heic2any({
      blob: file,
      toType: "image/jpeg",
      quality: QUALITY,
    });
    const single: Blob = Array.isArray(blob) ? blob[0] : blob;
    const newName = file.name.replace(/\.heic$/i, ".jpg").replace(/\.heif$/i, ".jpg");
    return new File([single], newName, { type: "image/jpeg", lastModified: file.lastModified });
  } catch (err) {
    console.warn("HEIC conversion failed, sending original:", err);
    return file;
  }
}


/** Главная функция: принимает File от пользователя, возвращает оптимизированный.
 *
 *  Стратегия:
 *  1. HEIC → конвертим в JPEG (через heic2any)
 *  2. Картинка PNG/JPEG/WebP больше 1.5MB ИЛИ шире 2400px → пережимаем
 *  3. Маленькие картинки (<1.5MB и <=2400px) — отдаём как есть
 *  4. Видео и прочие файлы — отдаём как есть
 *
 *  Если в процессе что-то ломается — возвращаем оригинал (fallback).
 */
export async function optimizeBeforeUpload(file: File): Promise<File> {
  // 1. HEIC → JPEG
  let work = file;
  if (isHeic(file)) {
    work = await convertHeicToJpeg(file);
  }

  // 2. Если не картинка которую можем сжать — отдаём как есть
  if (!isCompressibleImage(work)) return work;

  // 3. Маленькие — не трогаем (избегаем лишней работы и потенциальной потери качества)
  const sizeMB = work.size / (1024 * 1024);
  if (sizeMB < MAX_SIZE_MB) {
    // Размер ОК; проверим только, не нужно ли уменьшить разрешение (камеры 4K делают мегабайтные файлы)
    // Но дешевле всего: оставляем — bandwidth и так ок.
    return work;
  }

  // 4. Сжимаем (lazy import: ~150KB либа грузится только при первом крупном фото)
  try {
    const mod = await import("browser-image-compression");
    const imageCompression = mod.default;
    const opts: CompressOptions = {
      maxSizeMB: MAX_SIZE_MB,
      maxWidthOrHeight: MAX_WIDTH,
      useWebWorker: true,
      initialQuality: QUALITY,
      preserveExif: true,
    };
    const compressed: Blob = await imageCompression(work, opts);
    if (compressed instanceof File) return compressed;
    return new File([compressed], work.name, {
      type: compressed.type || work.type,
      lastModified: work.lastModified,
    });
  } catch (err) {
    console.warn("Image compression failed, sending original:", err);
    return work;
  }
}


/** Batch-версия — оптимизирует несколько файлов параллельно, но с лимитом
 *  concurrency (не больше N одновременно — иначе мобильный браузер заваливается). */
export async function optimizeBatch(files: File[], concurrency = 3): Promise<File[]> {
  const out: File[] = new Array(files.length);
  let cursor = 0;
  async function worker() {
    while (cursor < files.length) {
      const i = cursor++;
      out[i] = await optimizeBeforeUpload(files[i]);
    }
  }
  const workers = Array.from({ length: Math.min(concurrency, files.length) }, () => worker());
  await Promise.all(workers);
  return out;
}
